import * as React from "react";
import * as _ from "underscore";
import * as moment from "moment";

import AddressFieldsWithValidation from "../../AddressFieldsWithValidation";
import LabeledInput from "../../LabeledInput";
import NameField from "./NameField";

require("../../../assets/stylesheets/components/underwriter_dashboard/shared/field_entry.scss");

const classnames = require("classnames");
const { OverlayTrigger, Tooltip } = require("react-bootstrap");

interface FieldEntryProps {
  layout: any;
  values: any;
  conditionalContent?: any;
  upsertValue: Function;
  deleteValue: Function;
}

class FieldEntry extends React.Component<FieldEntryProps, any> {
  constructor(props) {
    super(props);
    this.state = { focusedSection: null };
  }

  static checkSection(section) {
    const { sectionName, fields } = section;

    if (!sectionName && fields) {
      throw new Error("section must have sectionName");
    }
    if (sectionName && (!fields || !fields.length)) {
      throw new Error(`section ${sectionName} must have fields`);
    }
    if (!sectionName && (!fields || !fields.length)) {
      throw new Error("section must have sectionName and fields");
    }
  }

  static checkField(field) {
    const { fieldKey, fieldType, fieldName } = field;

    if (!fieldKey && fieldType && fieldName) {
      throw new Error(`field ${fieldName} must have fieldKey`);
    }
    if (fieldKey && !fieldType && fieldName) {
      throw new Error(`field ${fieldName} must have fieldType`);
    }
    if (fieldKey && fieldType && !fieldName) {
      throw new Error("field must have fieldName");
    }
    if (!fieldKey && !fieldType && fieldName) {
      throw new Error(`field ${fieldName} must have fieldKey and fieldType`);
    }
    if (fieldKey && !fieldType && !fieldName) {
      throw new Error(`field must have fieldKey and fieldName`);
    }
    if (!fieldKey && !fieldType && !fieldName) {
      throw new Error(`field must have fieldKey, fieldType, and fieldName`);
    }
  }

  setFocusedSection(sectionName) {
    this.setState({ focusedSection: sectionName });
  }

  clearFocusedSection() {
    this.setState({ focusedSection: null });
  }

  onChangeGeneric(e) {
    const { name, value } = e.target;

    if (value.length) {
      this.props.upsertValue(name, value);
    } else {
      this.props.deleteValue(name);
    }
  }

  onChangeAddress(key, address) {
    if (!address || !_.some(address)) {
      this.props.deleteValue(key);
      return;
    }

    let postalCode = null;
    let plusFour = null;

    if (address.postal_code && /^\d+(-\d+)?$/.test(address.postal_code)) {
      const s = address.postal_code.split("-");
      postalCode = s[0];
      plusFour = s[1] || "";
    } else if (address.postal_code) {
      postalCode = address.postal_code;
    }

    this.props.upsertValue(key, {
      postal_code: postalCode,
      plus_four: plusFour,
      line1: address.address_line1,
      line2: address.address_line2,
      city: address.city,
      state: address.state,
    });
  }

  onChangeBoolean(e) {
    const { name, checked } = e.target;

    if (checked) {
      this.props.upsertValue(name, true);
    } else {
      this.props.deleteValue(name);
    }
  }

  onChangeInteger(e) {
    const { name, value } = e.target;

    if (/^\d+$/.test(value)) {
      this.props.upsertValue(name, parseInt(value, 10));
    } else if (!value.length) {
      this.props.deleteValue(name);
    }
  }

  onChangeName(key, name) {
    if (!name || !_.some(name)) {
      this.props.deleteValue(key);
      return;
    }

    this.props.upsertValue(key, {
      first: name.first || null,
      middle: name.middle || null,
      last: name.last || null,
      suffix: name.suffix || null,
    });
  }

  lintSection(section) {
    // FIXME: jjw
    return {
      warnings: null,
      errors: null,
    };
  }

  lintField(section, field) {
    // FIXME: jjw
    return {
      warnings: null,
      errors: null,
    };
  }

  static renderLintIcon(status, message = null) {
    const divClasses = classnames({
      "field-entry__linter-icon": true,
      "field-entry__linter-icon--ok": status === "ok",
      "field-entry__linter-icon--warning": status === "warning",
      "field-entry__linter-icon--error": status === "error",
    });

    const iconClasses = classnames({
      "fa fa-check-circle": status === "ok",
      "fa fa-question-circle": status === "warning",
      "fa fa-times-circle": status === "error",
    });

    if (status === "ok") {
      return (
        <div className={divClasses}>
          <i className={iconClasses} />
        </div>
      );
    }

    return (
      <div className={divClasses}>
        <OverlayTrigger placement="left" overlay={<Tooltip id="tooltip">{message}</Tooltip>}>
          <i className={iconClasses} />
        </OverlayTrigger>
      </div>
    );
  }

  renderSectionLintIcon(section) {
    const lint = this.lintSection(section);

    if (lint.errors && lint.errors.length) {
      return FieldEntry.renderLintIcon("error", lint.errors[0]);
    }
    if (lint.warnings && lint.warnings.length) {
      return FieldEntry.renderLintIcon("warning", lint.warnings[0]);
    }

    return null;
  }

  renderFieldLintIcon(section, field) {
    const lint = this.lintField(section, field);

    if (lint.errors && lint.errors.length) {
      return FieldEntry.renderLintIcon("error", lint.errors[0]);
    }
    if (lint.warnings && lint.warnings.length) {
      return FieldEntry.renderLintIcon("warning", lint.warnings[0]);
    }
    if (this.props.values[field.fieldKey]) {
      return FieldEntry.renderLintIcon("ok");
    }

    return null;
  }

  renderField(section, field) {
    const { values } = this.props;

    const { fieldKey, fieldType, fieldName, fieldDesc } = field;

    const sharedProps = {
      labelValue: fieldName,
      hint: fieldDesc,
      onFocus: this.setFocusedSection.bind(this, section.sectionName),
      onBlur: this.clearFocusedSection.bind(this),
    };

    // this.props.values will be typed exactly like the API; means addresses,
    // booleans, integers, names, and strings are OK, but dates need times
    // removed, and money comes through as a string (which MoneyInput handles)

    // inputs follow the same conventions when getting values from the UI,
    // parsing them (or not), and then saving back to this.props.values by
    // calling this.props.upsertValue or this.props.deleteValue as needed

    const val = values[fieldKey];

    switch (fieldType) {
      case "address": {
        let zip = "";
        if (val && val.postal_code && val.plus_four) {
          zip = `${val.postal_code}-${val.plus_four}`;
        } else if (val && val.postal_code) {
          zip = val.postal_code;
        } else if (val && val.plus_four) {
          zip = `-${val.plus_four}`;
        }
        return (
          <AddressFieldsWithValidation
            {...sharedProps}
            address_line1={val && val.line1 ? val.line1 : ""}
            address_line2={val && val.line2 ? val.line2 : ""}
            city={val && val.city ? val.city : ""}
            state={val && val.state ? val.state : ""}
            postal_code={zip}
            skipValidation
            callback={this.onChangeAddress.bind(this, fieldKey)}
          />
        );
      }
      case "boolean": {
        return (
          <LabeledInput
            {...sharedProps}
            inputType="boolean"
            inputName={fieldKey}
            inputValue={val || ""}
            onChange={this.onChangeBoolean.bind(this)}
          />
        );
      }
      case "date": {
        return (
          <LabeledInput
            {...sharedProps}
            inputType="date"
            inputName={fieldKey}
            inputValue={val ? moment.utc(val).format("YYYY-MM-DD") : ""}
            placeholder="yyyy-mm-dd"
            onChange={this.onChangeGeneric.bind(this)}
          />
        );
      }
      case "decimal": {
        return (
          <LabeledInput
            {...sharedProps}
            inputType="text"
            inputName={fieldKey}
            inputValue={values[fieldKey] || ""}
            onChange={this.onChangeGeneric.bind(this)}
          />
        );
      }
      case "integer": {
        return (
          <LabeledInput
            {...sharedProps}
            inputType="text"
            inputName={fieldKey}
            inputValue={typeof val === "number" ? `${val}` : ""}
            onChange={this.onChangeInteger.bind(this)}
          />
        );
      }
      case "money": {
        return (
          <LabeledInput
            {...sharedProps}
            inputType="money"
            inputName={fieldKey}
            inputValue={values[fieldKey] || ""}
            clearable
            onChange={this.onChangeGeneric.bind(this)}
          />
        );
      }
      case "name": {
        return (
          <NameField
            {...sharedProps}
            name={values[fieldKey] || ""}
            onChange={this.onChangeName.bind(this, fieldKey)}
          />
        );
      }
      case "string": {
        return (
          <LabeledInput
            {...sharedProps}
            inputType="text"
            inputName={fieldKey}
            inputValue={values[fieldKey] || ""}
            onChange={this.onChangeGeneric.bind(this)}
          />
        );
      }
    }

    throw new Error(`unknown type ${fieldType} for field key ${fieldKey}`);
  }

  renderFieldsForSection(section) {
    const { conditionalContent, values } = this.props;

    return (
      <div>
        {section.fields.map(field => {
          FieldEntry.checkField(field);

          const fieldClasses = classnames({
            "field-entry__field-input": true,
            "field-entry__field-input--checkbox": field.fieldType === "boolean",
          });

          let content;
          if (conditionalContent && typeof conditionalContent[field.fieldKey] === "function") {
            content = conditionalContent[field.fieldKey](values[field.fieldKey]);
          }

          return (
            <div key={`${section.sectionName}-${field.fieldKey}`}>
              <div className="field-entry__field">
                <div className={fieldClasses}>{this.renderField(section, field)}</div>
                {this.renderFieldLintIcon(section, field)}
              </div>
              {content}
            </div>
          );
        })}
      </div>
    );
  }

  render() {
    return (
      <div className="field-entry">
        {this.props.layout.map(section => {
          FieldEntry.checkSection(section);

          const sectionClasses = classnames({
            "field-entry__section": true,
            "field-entry__section--focused": section.sectionName === this.state.focusedSection,
          });

          return (
            <div key={section.sectionName} className={sectionClasses}>
              <div className="field-entry__section-container">
                {this.renderSectionLintIcon(section)}
                <div className="field-entry__section-header">
                  <strong className="field-entry__section-name">{section.sectionName}</strong>
                  <div
                    className="field-entry__section-desc"
                    dangerouslySetInnerHTML={{ __html: section.sectionDesc }}
                  />
                </div>
                {this.renderFieldsForSection(section)}
              </div>
            </div>
          );
        })}
      </div>
    );
  }
}

export default FieldEntry;
